/**
 * Per-customer payment settings — recurring failure policy + category list.
 *
 * URL: /payment-settings
 *
 * Backed by /api/customers/:id/payment-settings (added in migration 0070).
 * Resolves the active customer from the customer-context store; super_admin
 * + reseller_owner who haven't entered a customer yet see a prompt.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  customerPaymentSettings,
  type CustomerPaymentSettings,
  type RecurringOnMaxFail,
} from '@/api/customerPaymentSettings';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useToast } from '@/hooks/use-toast';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, X, Save } from 'lucide-react';

const ON_MAX_FAIL_OPTIONS: Array<{ value: RecurringOnMaxFail; label: string; hint: string }> = [
  {
    value: 'suspend_and_notify',
    label: 'Suspend + notify',
    hint: 'Pause the schedule so it stops trying. Customer admin gets an email/SMS so they can fix the card.',
  },
  {
    value: 'suspend',
    label: 'Suspend silently',
    hint: 'Pause the schedule with no notification. Useful if you have your own monitoring.',
  },
  {
    value: 'notify_only',
    label: 'Notify (stay active)',
    hint: 'Schedule stays active but next charge is pushed out a year. Customer admin notified to update the card and resume.',
  },
  {
    value: 'cancel',
    label: 'Cancel',
    hint: 'Terminal — the schedule is cancelled and won\'t resume.',
  },
];

export function PaymentSettingsPage() {
  const customerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const { toast } = useToast();

  if (!customerId) {
    return (
      <>
        <PageHeader
          eyebrow="Payments"
          title="Payment settings"
          description="Per-account recurring failure policy + designation list."
        />
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground text-center">
            Select a customer first (Customers → Enter) to manage payment settings.
          </CardContent>
        </Card>
      </>
    );
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['customerPaymentSettings', customerId],
    queryFn: () => customerPaymentSettings.get(customerId),
  });

  return (
    <>
      <PageHeader
        eyebrow="Payments"
        title="Payment settings"
        description="Per-account recurring failure policy + designation list."
      />
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : error || !data ? (
        <div role="alert" className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Could not load settings.'}
        </div>
      ) : (
        <SettingsForm
          customerId={customerId}
          initial={data}
          onSaved={(next) => {
            qc.setQueryData(['customerPaymentSettings', customerId], next);
            toast({ title: 'Settings saved' });
          }}
          onError={(msg) => {
            toast({ title: 'Save failed', description: msg, variant: 'destructive' });
          }}
        />
      )}
    </>
  );
}

function SettingsForm({
  customerId, initial, onSaved, onError,
}: {
  customerId: string;
  initial: CustomerPaymentSettings;
  onSaved: (next: CustomerPaymentSettings) => void;
  onError: (msg: string) => void;
}) {
  const [retryMax, setRetryMax] = useState(String(initial.recurring_retry_max));
  const [retryDelay, setRetryDelay] = useState(String(initial.recurring_retry_delay_days));
  const [onMaxFail, setOnMaxFail] = useState<RecurringOnMaxFail>(initial.recurring_on_max_fail);
  const [categories, setCategories] = useState<string[]>([...initial.payment_categories]);
  const [newCategory, setNewCategory] = useState('');
  const [notifyPhone, setNotifyPhone] = useState(initial.notification_phone ?? '');
  const [extras, setExtras] = useState<string[]>([...initial.notification_recipients_extra]);
  const [newExtra, setNewExtra] = useState('');

  // Reset state if a fresh fetch comes in (e.g. page refocus).
  useEffect(() => {
    setRetryMax(String(initial.recurring_retry_max));
    setRetryDelay(String(initial.recurring_retry_delay_days));
    setOnMaxFail(initial.recurring_on_max_fail);
    setCategories([...initial.payment_categories]);
    setNotifyPhone(initial.notification_phone ?? '');
    setExtras([...initial.notification_recipients_extra]);
  }, [initial]);

  const retryMaxN = Number(retryMax);
  const retryDelayN = Number(retryDelay);
  const retryMaxValid = Number.isInteger(retryMaxN) && retryMaxN >= 0 && retryMaxN <= 20;
  const retryDelayValid = Number.isInteger(retryDelayN) && retryDelayN >= 0 && retryDelayN <= 30;
  const categoriesValid = categories.length >= 1;
  const phoneTrimmed = notifyPhone.trim();
  const phoneValid = phoneTrimmed === '' || /^\+[1-9]\d{7,14}$/.test(phoneTrimmed);
  const extrasValid = extras.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  const canSave = retryMaxValid && retryDelayValid && categoriesValid && phoneValid && extrasValid;

  const dirty = useMemo(() =>
    retryMaxN !== initial.recurring_retry_max
    || retryDelayN !== initial.recurring_retry_delay_days
    || onMaxFail !== initial.recurring_on_max_fail
    || categories.length !== initial.payment_categories.length
    || categories.some((c, i) => c !== initial.payment_categories[i])
    || phoneTrimmed !== (initial.notification_phone ?? '')
    || extras.length !== initial.notification_recipients_extra.length
    || extras.some((e, i) => e !== initial.notification_recipients_extra[i]),
  [retryMaxN, retryDelayN, onMaxFail, categories, phoneTrimmed, extras, initial],
  );

  const save = useMutation({
    mutationFn: () => customerPaymentSettings.patch(customerId, {
      recurring_retry_max: retryMaxN,
      recurring_retry_delay_days: retryDelayN,
      recurring_on_max_fail: onMaxFail,
      payment_categories: categories,
      notification_phone: phoneTrimmed || null,
      notification_recipients_extra: extras,
    }),
    onSuccess: onSaved,
    onError: (err) => onError(err instanceof Error ? err.message : 'unknown'),
  });

  const addCategory = () => {
    const v = newCategory.trim();
    if (!v) return;
    if (categories.includes(v)) return;
    if (categories.length >= 50) {
      onError('Cap is 50 categories per account.');
      return;
    }
    setCategories([...categories, v]);
    setNewCategory('');
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>Recurring failure policy</CardTitle>
          <CardDescription>
            What happens when a recurring payment fails. The legacy
            per-schedule retry array (set when the schedule was created)
            takes precedence; once exhausted, this policy kicks in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ps-retry-max">Max retry attempts</Label>
              <Input
                id="ps-retry-max"
                type="number" min="0" max="20"
                value={retryMax}
                onChange={(e) => setRetryMax(e.target.value)}
                className="w-32"
              />
              {!retryMaxValid && (
                <p className="text-xs text-destructive">Must be 0–20.</p>
              )}
              <p className="text-xs text-muted-foreground">
                After the per-schedule array is exhausted. 0 = no policy retries.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ps-retry-delay">Days between attempts</Label>
              <Input
                id="ps-retry-delay"
                type="number" min="0" max="30"
                value={retryDelay}
                onChange={(e) => setRetryDelay(e.target.value)}
                className="w-32"
              />
              {!retryDelayValid && (
                <p className="text-xs text-destructive">Must be 0–30.</p>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label>On exhausted retries</Label>
            <Select value={onMaxFail} onValueChange={(v) => setOnMaxFail(v as RecurringOnMaxFail)}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ON_MAX_FAIL_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {ON_MAX_FAIL_OPTIONS.find((o) => o.value === onMaxFail)?.hint}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Designation / category list</CardTitle>
          <CardDescription>
            The categories your IVR payment nodes can tag a charge with
            (e.g. "Building Fund", "Tuition", "Donation"). Per-node config
            picks one or shows a caller-choice menu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {categories.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs"
              >
                {c}
                {categories.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setCategories(categories.filter((x) => x !== c))}
                    className="text-slate-400 hover:text-red-600"
                    aria-label={`Remove ${c}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {categories.length === 0 && (
              <span className="text-xs text-muted-foreground italic">
                No categories yet — add at least one below.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCategory();
                }
              }}
              placeholder="e.g. Building Fund"
              maxLength={80}
              className="max-w-xs"
            />
            <Button size="sm" variant="outline" onClick={addCategory} disabled={!newCategory.trim()}>
              <Plus className="h-3.5 w-3.5 me-1" />
              Add
            </Button>
          </div>
          {!categoriesValid && (
            <p className="text-xs text-destructive">Need at least one category.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Customer notifications</CardTitle>
          <CardDescription>
            Where to send payment-success notifications. The primary email is
            your account contact email (configured under account settings).
            Add a phone number for SMS, and extra email addresses for cc.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1 max-w-sm">
            <Label htmlFor="ps-notify-phone">SMS notification number (E.164)</Label>
            <Input
              id="ps-notify-phone"
              value={notifyPhone}
              onChange={(e) => setNotifyPhone(e.target.value)}
              placeholder="+18005551234"
              className="font-mono"
            />
            {!phoneValid && (
              <p className="text-xs text-destructive">
                Must be E.164 format (start with + and 8–15 digits) or empty.
              </p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Empty = no SMS notification. The from-number is your customer's
              first active SMS-enabled DID.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Extra email recipients (cc)</Label>
            <div className="flex flex-wrap gap-1.5">
              {extras.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs"
                >
                  {e}
                  <button
                    type="button"
                    onClick={() => setExtras(extras.filter((x) => x !== e))}
                    className="text-slate-400 hover:text-red-600"
                    aria-label={`Remove ${e}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {extras.length === 0 && (
                <span className="text-xs text-muted-foreground italic">No extras yet.</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="email"
                value={newExtra}
                onChange={(e) => setNewExtra(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const v = newExtra.trim().toLowerCase();
                    if (!v) return;
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
                    if (extras.includes(v)) return;
                    if (extras.length >= 20) return;
                    setExtras([...extras, v]);
                    setNewExtra('');
                  }
                }}
                placeholder="finance@acme.com"
                maxLength={200}
                className="max-w-xs"
              />
              <Button
                size="sm" variant="outline"
                disabled={!newExtra.trim()}
                onClick={() => {
                  const v = newExtra.trim().toLowerCase();
                  if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return;
                  if (extras.includes(v) || extras.length >= 20) return;
                  setExtras([...extras, v]);
                  setNewExtra('');
                }}
              >
                <Plus className="h-3.5 w-3.5 me-1" />
                Add
              </Button>
            </div>
            {!extrasValid && (
              <p className="text-xs text-destructive">One or more emails are invalid.</p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={!dirty || !canSave || save.isPending}>
          <Save className="h-4 w-4 me-1.5" />
          {save.isPending ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </div>
  );
}
